import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { Collapse } from "@/components/design/Collapse";
import { buttonClasses } from "@/components/design/button-classes";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { TextInput } from "@/components/design/TextField";
import {
  EmailTemplateKind,
  ListingApplicationMailKind,
  PreviewListingApplicationMailDocument,
  SendListingApplicationMailDocument,
  type PreviewListingApplicationMailMutation,
} from "@/graphql/generated/operations";
import { communicationLanguageName } from "@/lib/communication-language";
import { formatDateTime } from "@/lib/format-date";
import {
  MAIL_KIND_LABELS,
  MAX_APPOINTMENT_LENGTH,
  MAX_MEETING_LINK_LENGTH,
  RECIPIENT_SOURCE_LABELS,
  UNAVAILABLE_REASON_LABELS,
  invalidatesPreview,
  mailErrorMessage,
  meetingLinkValid,
} from "./listing-application-mail";

const TEMPLATE_KIND: Record<ListingApplicationMailKind, EmailTemplateKind> = {
  [ListingApplicationMailKind.Interview]: EmailTemplateKind.ListingInterview,
  [ListingApplicationMailKind.Offer]: EmailTemplateKind.ListingOffer,
  [ListingApplicationMailKind.Rejection]: EmailTemplateKind.ListingRejection,
  [ListingApplicationMailKind.Receipt]: EmailTemplateKind.ListingApplicationReceipt,
};

type Preview = PreviewListingApplicationMailMutation["previewListingApplicationMail"];

export interface MailDraft {
  kind: ListingApplicationMailKind;
  subject: string;
  body: string;
}

// Eine Interview-, Angebots- oder Absagemail an die bewerbende Person
// (@external-adr GENESIS-0080-an-application-mail-sends-exactly-the-confirmed-preview-to-a-proven-address).
//
// genesis speichert jede Vorschau und sendet beim Bestätigen genau sie: Text, Empfänger, Sprache. Deshalb gilt
// "Senden" nur für die aktuelle Vorschau. Termindaten müssen in ihr enthalten sein. Die Texte werden zentral
// in den Admin-Vorlagen gepflegt. Den Empfänger zeigt genesis nur maskiert, mit seiner Herkunft; die Sprache bestimmt genesis, nicht diese Sitzung. Eine Ablehnung
// lässt Eingaben stehen. Ein Status- oder Phasenwechsel sendet nie etwas; dieser Dialog ist der einzige Weg.
export function ListingApplicationMailDialog({ applicationId, kinds, initial, uncertainBefore, onClose }: {
  applicationId: string;
  kinds: readonly ListingApplicationMailKind[];
  /** Ein früherer Text, der erneut gesendet werden soll. */
  initial?: MailDraft;
  /** Die letzte Mail dieser Art kam möglicherweise an. */
  uncertainBefore?: boolean;
  onClose: () => void;
}) {
  const intl = useIntl();
  const canEditTemplates = useHasPermission(Permission.WriteAccessConfig);
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({ id: `share.listings.mail.${id}` }, values);
  const [requestPreview, previewState] = useMutation(PreviewListingApplicationMailDocument);
  const [requestSend, sendState] = useMutation(SendListingApplicationMailDocument);
  const [kind, setKind] = useState<ListingApplicationMailKind>(initial?.kind ?? kinds[0]!);
  const [appointment, setAppointment] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [showAppointment, setShowAppointment] = useState(false);
  // Termin und Link, aus denen der aktuelle Entwurf entstand: weichen die Felder davon ab, steht er nicht in der Mail.
  const [drafted, setDrafted] = useState({ appointment: "", meetingLink: "" });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Eine Anfrage-ID je bestätigter Vorschau: ein Doppelklick oder eine Wiederholung nach Netzfehler sendet einmal.
  const requestId = useRef<string | null>(null);
  // Nur die Antwort der zuletzt gestellten Vorschau zählt; eine ältere überschreibt nichts.
  const generation = useRef(0);
  const busy = previewState.loading || sendState.loading;

  async function load(next: { kind: ListingApplicationMailKind; subject?: string; body?: string }) {
    const mine = ++generation.current;
    const draft = next.subject === undefined;
    const inputs = { appointment, meetingLink };
    try {
      const interview = next.kind === ListingApplicationMailKind.Interview;
      const result = await requestPreview({ variables: { input: {
        applicationId,
        kind: next.kind,
        appointment: interview && draft ? inputs.appointment.trim() || null : null,
        meetingLink: interview && draft ? inputs.meetingLink.trim() || null : null,
        subject: next.subject ?? null,
        body: next.body ?? null,
      } } });
      if (mine !== generation.current) return;
      const answer = result.data?.previewListingApplicationMail;
      if (!answer) throw new Error("Missing mail preview");
      setPreview(answer);
      if (draft) setDrafted(inputs);
      setError(null);
      requestId.current = null;
    } catch (e) {
      if (mine !== generation.current) return;
      setPreview(null);
      setError(mailErrorMessage(intl, e));
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Opening asks genesis once for the draft; its answer sets the fields asynchronously.
    void load(initial ? { kind: initial.kind, subject: initial.subject, body: initial.body } : { kind });
    // Nur beim Öffnen: danach lädt HR ausdrücklich neu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function redraft(next: ListingApplicationMailKind) {
    setKind(next);
    void load({ kind: next });
  }

  async function send() {
    if (!preview?.previewId || busy) return;
    setError(null);
    requestId.current ??= crypto.randomUUID();
    try {
      const result = await requestSend({ variables: { input: {
        applicationId, previewId: preview.previewId, requestId: requestId.current,
      } } });
      if (!result.data?.sendListingApplicationMail) throw new Error("Missing mail send outcome");
      onClose();
    } catch (e) {
      setError(mailErrorMessage(intl, e));
      if (invalidatesPreview(e)) {
        setPreview(null);
        requestId.current = null;
      }
    }
  }

  const linkInvalid = !meetingLinkValid(meetingLink);
  const appointmentChanged = kind === ListingApplicationMailKind.Interview
    && (appointment !== drafted.appointment || meetingLink !== drafted.meetingLink);
  const current = preview !== null && preview.kind === kind && !appointmentChanged;
  const sendable = current && !!preview.previewId && !preview.unavailableReason && !busy;

  return <Modal id="listing-application-mail-dialog" open onClose={onClose} maxWidth="44rem" title={t("title")} footer={<>
    <Button id="listing-application-mail-cancel" variant="secondary" onClick={onClose} disabled={sendState.loading}>
      {intl.formatMessage({ id: "common.cancel" })}
    </Button>
    <Button id="listing-application-mail-preview" variant="secondary"
      disabled={busy || (kind === ListingApplicationMailKind.Interview && linkInvalid)}
      onClick={() => void load({ kind })}>{t("refreshPreview")}</Button>
    <Button id="listing-application-mail-send" variant="primary" disabled={!sendable} onClick={() => void send()}>
      {t("send")}
    </Button>
  </>}>
    <div className="space-y-5">
      {uncertainBefore ? <p className="smycles-hint smycles-field-value text-ink">{t("uncertainBefore")}</p> : null}
      {kinds.length > 1 ? <SegmentedControl id="listing-application-mail-kind" value={kind} onChange={redraft}
        options={kinds.map((value) => ({ value, label: intl.formatMessage(MAIL_KIND_LABELS[value]), disabled: busy }))} />
        : <p className="text-smycles-body font-medium">{intl.formatMessage(MAIL_KIND_LABELS[kind])}</p>}
      {canEditTemplates ? <a href={`/admin/settings?section=emailTemplates&template=${TEMPLATE_KIND[kind]}`}
        target="_blank" rel="noopener noreferrer" className={buttonClasses({ variant: "secondary", size: "compact" })}>
        {t("editTemplate")}
      </a> : <p className="text-smycles-meta text-ink-muted">{t("templateHint")}</p>}

      {kind === ListingApplicationMailKind.Interview ? <div>
        <Button variant="secondary" size="compact" aria-expanded={showAppointment}
          onClick={() => setShowAppointment(!showAppointment)}>{t("appointmentDetails")}</Button>
        <Collapse open={showAppointment}>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="smycles-field-label">{t("appointment")}</span>
              <TextInput id="listing-application-mail-appointment" className="mt-1" value={appointment}
                maxLength={MAX_APPOINTMENT_LENGTH} disabled={busy}
                placeholder={t("appointmentPlaceholder")} onChange={(e) => setAppointment(e.target.value)} />
            </label>
            <label className="block">
              <span className="smycles-field-label">{t("meetingLink")}</span>
              <TextInput id="listing-application-mail-link" className="mt-1" type="url" value={meetingLink}
                maxLength={MAX_MEETING_LINK_LENGTH} disabled={busy}
                placeholder="https://" aria-invalid={linkInvalid || undefined}
                onChange={(e) => setMeetingLink(e.target.value)} />
            </label>
            {linkInvalid ? <p role="alert" className="text-smycles-meta text-ink-accent">{t("meetingLinkInvalid")}</p> : null}
          </div>
        </Collapse>
      </div> : null}

      <section aria-label={t("previewRegion")} className="border-t border-ink/10 pt-4">
        {previewState.loading ? <p className="text-smycles-meta text-ink-muted" aria-busy="true">
          {intl.formatMessage({ id: "common.loading" })}</p>
          : !preview ? null
          : appointmentChanged ? <p className="text-smycles-meta text-ink">{t("appointmentChanged")}</p>
          : preview.unavailableReason ? <div className="smycles-hint">
            <div className="smycles-field-value text-ink">{t("unavailable")}</div>
            <div className="mt-1 text-smycles-meta text-ink">{intl.formatMessage(UNAVAILABLE_REASON_LABELS[preview.unavailableReason])}</div>
          </div>
          : <div className="space-y-2">
            <p id="listing-application-mail-recipient" className="text-smycles-meta text-ink">
              {t("recipient", {
                recipient: preview.maskedRecipient ?? "",
                source: preview.recipientSource ? intl.formatMessage(RECIPIENT_SOURCE_LABELS[preview.recipientSource]) : "",
                language: communicationLanguageName(intl, preview.language),
              })}
            </p>
            <p className="text-smycles-body font-medium">{preview.subject}</p>
            <p className="whitespace-pre-wrap text-smycles-body text-ink">{preview.body}</p>
            {preview.expiresOn ? <p className="text-smycles-meta text-ink-muted">
              {t("expires", { date: formatDateTime(preview.expiresOn, intl.locale) ?? "" })}</p> : null}
          </div>}
      </section>
      {error ? <p role="alert" className="text-smycles-meta text-ink-accent">{error}</p> : null}
    </div>
  </Modal>;
}
